import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BusinessDetailModal = ({
  business,
  isOpen,
  onClose,
  onConnect,
  onViewProfile
}) => {
  if (!isOpen || !business) return null;

  const getCategoryIcon = (category) => {
    const iconMap = {
      technology: 'Cpu',
      transportation: 'Car',
      energy: 'Zap',
      water_management: 'Droplets',
      waste_management: 'Trash2',
      governance: 'Building',
      education: 'BookOpen',
      healthcare: 'Heart',
      safety_security: 'Shield',
      economic_development: 'TrendingUp'
    };
    return iconMap?.[category] || 'Building2';
  };

  const formatCategory = (category) => {
    return category?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase());
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative flex items-center justify-center min-h-screen p-4">
        <div className="relative bg-card rounded-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-2xl font-bold text-foreground">Business Details</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Main Info */}
              <div className="lg:col-span-2 space-y-6">
                {/* Business Header */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {business?.logo_url ? (
                      <img
                        src={business?.logo_url}
                        alt={`${business?.name} logo`}
                        className="w-20 h-20 rounded-lg object-cover border border-border"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-muted rounded-lg flex items-center justify-center">
                        <Icon 
                          name={getCategoryIcon(business?.category)} 
                          size={32} 
                          className="text-muted-foreground"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-2xl font-bold text-foreground">
                        {business?.name}
                      </h3>
                      {business?.is_verified && (
                        <Icon name="CheckCircle" size={20} className="text-green-500" />
                      )}
                      {business?.is_featured && (
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                          Featured
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground mb-2">
                      {formatCategory(business?.category)}
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                      {business?.city && (
                        <div className="flex items-center space-x-1">
                          <Icon name="MapPin" size={14} />
                          <span>{business?.city}, {business?.country}</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-1">
                        <Icon name="Eye" size={14} />
                        <span>{business?.view_count || 0} views</span>
                      </div>
                      {business?.founded_year && (
                        <div className="flex items-center space-x-1">
                          <Icon name="Calendar" size={14} />
                          <span>Founded {business?.founded_year}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                {business?.description && (
                  <div>
                    <h4 className="text-lg font-semibold text-foreground mb-3">About</h4>
                    <p className="text-muted-foreground leading-relaxed">
                      {business?.description}
                    </p>
                  </div>
                )}

                {/* Services */}
                {business?.services?.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-foreground mb-3">Services</h4>
                    <div className="flex flex-wrap gap-2">
                      {business?.services?.map((service, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-50 text-blue-700 border border-blue-200"
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Technologies */}
                {business?.technologies?.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-foreground mb-3">Technologies</h4>
                    <div className="flex flex-wrap gap-2">
                      {business?.technologies?.map((tech, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-50 text-green-700 border border-green-200"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gallery */}
                {business?.images_urls?.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-foreground mb-3">Gallery</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {business?.images_urls?.map((image, index) => (
                        <img
                          key={index}
                          src={image}
                          alt={`${business?.name} image ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border border-border"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Contact & Actions */}
              <div className="space-y-6">
                {/* Contact Information */}
                <div className="bg-muted rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-foreground mb-4">Contact Information</h4>
                  <div className="space-y-3">
                    {business?.contact_email && (
                      <div className="flex items-center space-x-2">
                        <Icon name="Mail" size={16} className="text-muted-foreground" />
                        <a 
                          href={`mailto:${business?.contact_email}`}
                          className="text-sm text-primary hover:underline"
                        >
                          {business?.contact_email}
                        </a>
                      </div>
                    )}
                    {business?.contact_phone && (
                      <div className="flex items-center space-x-2">
                        <Icon name="Phone" size={16} className="text-muted-foreground" />
                        <a 
                          href={`tel:${business?.contact_phone}`}
                          className="text-sm text-primary hover:underline"
                        >
                          {business?.contact_phone}
                        </a>
                      </div>
                    )}
                    {business?.website_url && (
                      <div className="flex items-center space-x-2">
                        <Icon name="Globe" size={16} className="text-muted-foreground" />
                        <a 
                          href={business?.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline"
                        >
                          Visit Website
                        </a>
                      </div>
                    )}
                    {business?.address && (
                      <div className="flex items-start space-x-2">
                        <Icon name="MapPin" size={16} className="text-muted-foreground mt-0.5" />
                        <span className="text-sm text-muted-foreground">
                          {business?.address}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Business Stats */}
                <div className="bg-muted rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-foreground mb-4">Business Stats</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Profile Views</span>
                      <span className="text-sm font-medium text-foreground">
                        {business?.view_count || 0}
                      </span>
                    </div>
                    {business?.employee_count_range && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Team Size</span>
                        <span className="text-sm font-medium text-foreground">
                          {business?.employee_count_range}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Member Since</span>
                      <span className="text-sm font-medium text-foreground">
                        {new Date(business?.created_at)?.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short'
                        })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    onClick={onConnect}
                    variant="default"
                    size="lg"
                    iconName="MessageSquare"
                    iconPosition="left"
                    iconSize={16}
                    fullWidth
                  >
                    Send Message
                  </Button>
                  <Button
                    onClick={onViewProfile}
                    variant="outline"
                    size="lg"
                    iconName="ExternalLink"
                    iconPosition="left"
                    iconSize={16}
                    fullWidth
                  >
                    View Full Profile
                  </Button>
                  <Button
                    onClick={() => {
                      // Add to favorites functionality
                      console.log('Added to favorites');
                    }}
                    variant="ghost"
                    size="lg"
                    iconName="Heart"
                    iconPosition="left"
                    iconSize={16}
                    fullWidth
                  >
                    Add to Favorites
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessDetailModal;